import { z } from "zod";
import { type Contenido, esquemaIdDeBloque, type Nodo, textoDe } from "./contenido.js";

/**
 * Fase 4 · las marcas del revisor sobre el documento (pantalla 09). Una marca se ancla a un
 * párrafo o título por su id de bloque, con el tramo marcado en desplazamientos sobre el texto
 * de ese bloque (`textoDe`: los tramos de texto pegados, y una referencia cuenta como su código)
 * y la frase citada. El id sobrevive a las ediciones; los desplazamientos no siempre, por eso se
 * guarda la cita: si el autor cambió el párrafo, `reanclar` vuelve a buscarla en el mismo bloque.
 *
 * En los documentos que son un archivo (sin editor) no hay dónde anclar: la marca va sin ancla,
 * como comentario general.
 */

export const TIPOS_DE_CORRECCION = ["corregir", "sugerencia", "comentario"] as const;
export type TipoDeCorreccion = (typeof TIPOS_DE_CORRECCION)[number];
export const NOMBRE_TIPO_CORRECCION: Record<TipoDeCorreccion, string> = {
  corregir: "Corregir",
  sugerencia: "Sugerencia",
  comentario: "Comentario",
};

export const ESTADOS_DE_CORRECCION = ["abierta", "resuelta"] as const;
export type EstadoDeCorreccion = (typeof ESTADOS_DE_CORRECCION)[number];

/** Los bloques a los que se ancla una marca: los que tienen texto corrido, y el título de una sección. */
const BLOQUES_DE_TEXTO = new Set(["paragraph", "heading", "seccion"]);

export const esquemaAncla = z
  .object({
    bloqueId: esquemaIdDeBloque,
    inicio: z.number().int().min(0),
    fin: z.number().int().min(1),
    citado: z.string().min(1).max(2000),
  })
  .refine((a) => a.fin > a.inicio, "El tramo marcado está vacío")
  .refine((a) => a.fin - a.inicio === a.citado.length, "La cita no coincide con el tramo");
export type Ancla = z.infer<typeof esquemaAncla>;

/** Cuántos párrafos y títulos puede abarcar una sola marca. */
export const MAX_TRAMOS_POR_MARCA = 40;

export const esquemaNuevaCorreccion = z
  .object({
    revisionId: z.uuid(),
    /** Sin ancla: un comentario general (documentos con archivo, o algo que no es de una frase). */
    ancla: esquemaAncla.optional(),
    /**
     * Una selección que abarca varios párrafos o títulos: el primer tramo es `ancla` y los demás
     * van aquí, en orden. Todos llevan el mismo comentario y el mismo número.
     */
    mas: z.array(esquemaAncla).max(MAX_TRAMOS_POR_MARCA).default([]),
    tipo: z.enum(TIPOS_DE_CORRECCION),
    comentario: z.string().trim().min(1, "Escribe qué hay que corregir").max(2000),
  })
  .refine((c) => c.mas.length === 0 || !!c.ancla, "Los tramos van con un ancla");

export const esquemaRespuestaCorreccion = z.object({
  correccionId: z.uuid(),
  texto: z.string().trim().min(1, "Escribe la respuesta").max(2000),
});

/**
 * El texto de un párrafo o título por su id; null si no existe o no es un bloque de texto. Para
 * una sección es su título (lo que se marca de ella), no lo que contiene.
 */
export function textoDeBloque(contenido: Contenido, bloqueId: string): string | null {
  const nodo = buscar(contenido, bloqueId);
  if (!nodo || !BLOQUES_DE_TEXTO.has(nodo.type)) return null;
  if (nodo.type === "seccion") return String(nodo.attrs?.titulo ?? "");
  return textoDe(nodo);
}

function buscar(nodo: Nodo, id: string): Nodo | null {
  if (nodo.attrs?.id === id) return nodo;
  for (const hijo of nodo.content ?? []) {
    const hallado = buscar(hijo, id);
    if (hallado) return hallado;
  }
  return null;
}

/** ¿El ancla cae en un párrafo del contenido y cita lo que dice? Es lo que se exige al marcar. */
export function anclaValida(contenido: Contenido, ancla: Ancla): boolean {
  const texto = textoDeBloque(contenido, ancla.bloqueId);
  return texto !== null && texto.slice(ancla.inicio, ancla.fin) === ancla.citado;
}

/**
 * Dónde está hoy el tramo de una marca. Si el texto no se tocó, donde estaba; si cambió, la
 * aparición de la cita más cercana a donde estaba, en el mismo bloque; null si el bloque ya no
 * existe o la frase ya no está (la marca queda "sin lugar": se ve en el panel, no en el texto).
 */
export function reanclar(
  contenido: Contenido,
  ancla: Ancla,
): { inicio: number; fin: number } | null {
  const texto = textoDeBloque(contenido, ancla.bloqueId);
  if (texto === null) return null;
  if (texto.slice(ancla.inicio, ancla.fin) === ancla.citado) {
    return { inicio: ancla.inicio, fin: ancla.fin };
  }
  let mejor: number | null = null;
  for (let k = texto.indexOf(ancla.citado); k >= 0; k = texto.indexOf(ancla.citado, k + 1)) {
    if (mejor === null || Math.abs(k - ancla.inicio) < Math.abs(mejor - ancla.inicio)) mejor = k;
  }
  return mejor === null ? null : { inicio: mejor, fin: mejor + ancla.citado.length };
}
